import { PauseIcon, PlayIcon } from '@/components/Icons';
import Loading from '@/components/Loading';
import Show from '@/components/Show';
import { Badge } from '@/components/ui/badge';
import JenErrorToast from '@/components/ui/jen-error-toast';
import { SkeletonWrapper } from '@/components/ui/SkeletonWrapper';
import { getUploadSignUrl } from '@/service/audioList';
import { getAudioAcrStatus } from '@/service/generatorUpload';
import useGeneratorStore, { GeneratorTypeEnums, R3imagineStatusType } from '@/state/generator-store';
import { useRequest } from 'ahooks';
import React, { useEffect, useMemo, useRef } from 'react';
import { toast } from 'sonner';
import CardContainer from '../ui/CardContainer';
import { usePreviewPlayerCard } from './usePreviewPlayerCard';

interface PreviewPlayerCardProps {}

const PreviewPlayerCard: React.FC<PreviewPlayerCardProps> = () => {
  const containerRef = useRef<HTMLDivElement>(null);

  const {
    generatorType,
    uploadFileData,
    setR3ImageStatus,
    audioUploadingStatus,
    hummingAudioSelected,
    audioPlayerLoading,
    setAudioPlayerLoading,
    setAudioUploadingStatus,
  } = useGeneratorStore();

  const { data: getSignUrlData, run: getUploadSignUrlRun } = useRequest(getUploadSignUrl, {
    defaultParams: [
      {
        id: generatorType === GeneratorTypeEnums.R3IMAGINE ? String(uploadFileData?.data?.id) : String(hummingAudioSelected?.id),
      },
    ],
    staleTime: 3000,
  });

  const { isPlaying, currentTime, onPlayPause } = usePreviewPlayerCard({
    signUrl: getSignUrlData?.data?.sign_url,
    containerRef,
    onLoadingChange: setAudioPlayerLoading,
  });

  const { run: getAudioAcrStatusRun, cancel: getAudioAcrStatusCancel } = useRequest(getAudioAcrStatus, {
    manual: true,
    pollingInterval: 1000,
    pollingErrorRetryCount: 3,
    onSuccess: (res) => {
      setAudioUploadingStatus(res.data.status);
      if (['success', 'error'].includes(res.data.status)) {
        getAudioAcrStatusCancel();
        if (res.data.status === 'success') {
          getUploadSignUrlRun({
            id: String(uploadFileData?.data?.id),
          });
        }
      }
    },
    onError: () => {
      toast(
        <JenErrorToast
          error={new Error('Copyright Issue. We noticed some copyright issues with your uploaded track. Please try a new one.')}
        />,
        {
          className: '!border-0 !shadow-none',
          unstyled: true,
        },
      );
    },
  });

  useEffect(() => {
    if (generatorType === GeneratorTypeEnums.R3IMAGINE) {
      getAudioAcrStatusRun(uploadFileData?.data?.id ?? 0);
    }
  }, [generatorType]);

  const audioUploadingLoading = useMemo(() => ['uploading', 'checking'].includes(audioUploadingStatus), [audioUploadingStatus]);

  return (
    <CardContainer>
      <div className="p-3">
        <div>
          <Badge
            variant="info"
            size="mini"
            className="px-2 py-0.5 cursor-pointer rounded-md mr-2 my-1 font-pressura-mono font-medium uppercase"
            onClick={() => {
              onPlayPause();
              setR3ImageStatus(R3imagineStatusType.WaitingUpload);
            }}
          >
            CANCEL
          </Badge>
        </div>
        <div
          className="my-1"
          style={{
            background: 'repeating-linear-gradient(to right, transparent, transparent 2px, gray 2px, gray 4px)',
            height: '2px',
          }}
        />

        <div className="flex my-4">
          <div className="flex items-center justify-between w-16 h-16 p-1">
            <Show when={audioUploadingLoading}>
              <Loading />
            </Show>
            <Show when={['success'].includes(audioUploadingStatus)}>
              <>
                <button
                  onClick={onPlayPause}
                  type="button"
                  disabled={audioUploadingLoading || audioPlayerLoading}
                  className="flex items-center justify-center w-14 h-14 bg-jen-orange rounded-full"
                >
                  {isPlaying ? <PauseIcon className="w-8 h-8" fillColor="#fff" /> : <PlayIcon className="w-8 h-8" fillColor="#fff" />}
                </button>
              </>
            </Show>
          </div>
          <SkeletonWrapper loading={audioPlayerLoading || audioUploadingLoading} className="w-full">
            <div className="cursor-pointer w-full" ref={containerRef} />
          </SkeletonWrapper>
        </div>
        <div
          className="my-1"
          style={{
            background: 'repeating-linear-gradient(to right, transparent, transparent 2px, gray 2px, gray 4px)',
            height: '2px',
          }}
        />
        <div className="flex items-center justify-between text-xl">
          <Show
            when={['success'].includes(audioUploadingStatus)}
            fallback={<span className="whitespace-nowrap overflow-hidden text-ellipsis">{audioUploadingStatus}</span>}
          >
            <span className="whitespace-nowrap overflow-hidden text-ellipsis text-white">
              {generatorType === GeneratorTypeEnums.R3IMAGINE ? uploadFileData?.data?.name : hummingAudioSelected?.name}
            </span>
          </Show>
          <span className="text-white">{new Date(currentTime * 1000).toISOString().substr(14, 5)}</span>
        </div>
      </div>
    </CardContainer>
  );
};

export default PreviewPlayerCard;
